import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { FiFilter, FiX } from "react-icons/fi";

const Users = () => {
  // State management
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
  });
  const [filtersApplied, setFiltersApplied] = useState(false);

  const { currentUser } = useSelector((state) => state.user);

  // Safe date formatting
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  // Safe time formatting
  const formatTime = (dateString) => {
    try {
      return new Date(dateString).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch {
      return 'Invalid time';
    }
  };


  // Fetch sales data only when filters are applied
  const fetchSales = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
      });

      const response = await fetch(
        `/api/sales/getSales/${currentUser.username}?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch sales data');
      }

      const data = await response.json();
      setSalesData(data.sales || []);
      setPagination(prev => ({
        ...prev,
        total: data.total || 0,
      }));
    } catch (err) {
      setError(err.message);
      setSalesData([]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals safely
  const { totalWinning, totalCut, totalTransactions } = useMemo(() => {
    return salesData.reduce(
      (acc, sale) => {
        return {
          totalWinning: acc.totalWinning + (sale.won || 0),
          totalCut: acc.totalCut + (sale.cut || 0),
          totalTransactions: acc.totalTransactions + 1,
        };
      },
      { totalWinning: 0, totalCut: 0, totalTransactions: 0 }
    );
  }, [salesData]);


  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Apply filters and fetch data
  const applyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    setFiltersApplied(true);
    fetchSales();
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      startDate: "",
      endDate: "",
    });
    setFiltersApplied(false);
    setSalesData([]);
    setError(null);
  };

  // Fetch data when page changes (only if filters are applied)
  useEffect(() => {
    if (filtersApplied) {
      fetchSales();
    }
  }, [pagination.page]);

  // Pagination controls
  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const canPrevious = pagination.page > 1;
  const canNext = pagination.page < totalPages;

  const goToPage = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  return (
    <div className="tw-container tw-mx-auto tw-p-4 tw-mt-10">
      {/* Header */}
      <div className="tw-text-green-700 tw-font-bold tw-text-3xl tw-mb-6">
        Balance <span className="tw-text-pink-600">(${currentUser.balance?.toLocaleString() || 0})</span>
      </div>

      {/* Filter Controls */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-4 tw-mb-6">
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
              Start Date
            </label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md"
            />
          </div>
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
              End Date
            </label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md"
            />
          </div>
        </div>
        <div className="tw-flex tw-justify-end tw-mt-4 tw-space-x-3">
          <button
            onClick={clearFilters}
            className="tw-px-4 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-gray-700 hover:tw-bg-gray-50 tw-flex tw-items-center"
          >
            <FiX className="tw-mr-2" /> Clear
          </button>
          <button
            onClick={applyFilters}
            disabled={loading}
            className="tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded-md hover:tw-bg-blue-700 disabled:tw-opacity-50 tw-flex tw-items-center"
          >
            <FiFilter className="tw-mr-2" />
            {loading ? "Applying..." : "Apply Filters"}
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="tw-flex tw-justify-center tw-items-center tw-py-12">
          <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-t-2 tw-border-b-2 tw-border-blue-500"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="tw-bg-red-50 tw-border-l-4 tw-border-red-500 tw-p-4 tw-mb-6">
          <div className="tw-flex">
            <div className="tw-flex-shrink-0">
              <svg className="tw-h-5 tw-w-5 tw-text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="tw-ml-3">
              <p className="tw-text-sm tw-text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Results Section */}
      {filtersApplied && (
        <>
          {/* Summary Cards */}
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 tw-mb-6">
            <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-4">
              <h3 className="tw-text-sm tw-font-medium tw-text-gray-500">Total Winning</h3>
              <p className="tw-text-2xl tw-font-semibold tw-text-green-600">
                ${totalWinning.toLocaleString()}
              </p>
            </div>
            <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-4">
              <h3 className="tw-text-sm tw-font-medium tw-text-gray-500">Total Cut</h3>
              <p className="tw-text-2xl tw-font-semibold tw-text-blue-600">
                ${totalCut.toLocaleString()}
              </p>
            </div>
            <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-4">
              <h3 className="tw-text-sm tw-font-medium tw-text-gray-500">Transactions</h3>
              <p className="tw-text-2xl tw-font-semibold tw-text-purple-600">
                {totalTransactions.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Sales Table */}
          <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-overflow-hidden tw-mb-6">
            <div className="tw-overflow-x-auto">
              <table className="tw-min-w-full">
                <thead className="tw-bg-blue-800">
                  <tr>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">
                      Date
                    </th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">
                      Time
                    </th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">
                      Bet
                    </th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">
                      Player
                    </th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">
                      Total Won
                    </th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">
                      Cut
                    </th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">
                      Won
                    </th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">
                      #Call
                    </th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">
                      Winners
                    </th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">
                      Balance
                    </th>
                    
                  </tr>
                </thead>
                <tbody>
                  {salesData.length > 0 ? (
                    salesData.map((sale) => (
                      <tr key={sale._id} className="tw-border-b tw-border-gray-200 hover:tw-bg-gray-50">
                        <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap">{formatDate(sale.createdAt)}</td>
                        <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap">{formatTime(sale.createdAt)}</td>
                        <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap">${sale.bet?.toLocaleString() || 0}</td>
                        <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap">{sale.player || '-'}</td>
                        <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap">${sale.total?.toLocaleString() || 0}</td>
                        <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap">${sale.cut?.toLocaleString() || 0}</td>
                        <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap">${sale.won?.toLocaleString() || 0}</td>
                        <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap">{sale.call || '-'}</td>
                        <td className="tw-px-4 tw-py-3">{Array.isArray(sale.winner) ? sale.winner.join(", ") : '-'}</td>
                        <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap">${(Number(sale.winners?.[0]?.balance ?? sale.balance ?? 0) + Number(sale.cut ?? 0)).toLocaleString()}</td>
                        
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="12" className="tw-px-4 tw-py-3 tw-text-center">
                        No sales data available for the selected filters
                      </td>
                    </tr>
                  )}
                </tbody>

              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="tw-flex tw-items-center tw-justify-between tw-bg-white tw-px-4 tw-py-3 tw-rounded-lg tw-shadow-md">
              <div className="tw-flex-1 tw-flex tw-justify-between">
                <button
                  onClick={() => goToPage(pagination.page - 1)}
                  disabled={!canPrevious}
                  className={`tw-relative tw-inline-flex tw-items-center tw-px-4 tw-py-2 tw-border tw-text-sm tw-font-medium tw-rounded-md ${!canPrevious ? 'tw-bg-gray-100 tw-text-gray-400' : 'tw-bg-white tw-text-gray-700 hover:tw-bg-gray-50'
                    }`}
                >
                  Previous
                </button>
                <div className="tw-hidden sm:tw-flex sm:tw-flex-1 sm:tw-items-center sm:tw-justify-center">
                  <span className="tw-text-sm tw-text-gray-700">
                    Page <span className="tw-font-medium">{pagination.page}</span> of{' '}
                    <span className="tw-font-medium">{totalPages}</span>
                  </span>
                </div>
                <button
                  onClick={() => goToPage(pagination.page + 1)}
                  disabled={!canNext}
                  className={`tw-relative tw-inline-flex tw-items-center tw-px-4 tw-py-2 tw-border tw-text-sm tw-font-medium tw-rounded-md ${!canNext ? 'tw-bg-gray-100 tw-text-gray-400' : 'tw-bg-white tw-text-gray-700 hover:tw-bg-gray-50'
                    }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Initial State */}
      {!filtersApplied && !loading && (
        <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-8 tw-text-center">
          <p className="tw-text-gray-500">
            Please apply filters to view sales data
          </p>
        </div>
      )}
    </div>
  );
};

export default Users;